import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import { Ref, useImperativeHandle } from 'react';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';

export type RevenueSettingsValues = {
  employerRegistrationNumber: string;
  p12FileLink: string | File;
  passwordP12: string;
};

export type RevenueSettingsFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => RevenueSettingsValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<RevenueSettingsFormRef>;
  initialValues: Partial<RevenueSettingsValues>;
  stepNumber: number;
};

const RevenueSettings: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
}) => {
  const [form] = Form.useForm<RevenueSettingsValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <div className="RevenueSettings">
      <StepTitle
        step={stepNumber}
        title="Revenue Settings"
        description="Enter your Revenue Online Service (ROS) credentials for payroll submissions."
      />
      <div className="mt-[24px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={initialValues}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <div className="mb-[24px]">
            <div className="flex items-center gap-x-[8px] mb-[16px]">
              <IconWrapper icon="IconLayerGroup" />
              <div className="text-[16px] font-semibold">
                Revenue Credentials
              </div>
            </div>
            <Row gutter={16}>
              <Col sm={24} md={12}>
                <Form.Item
                  label="Employer Registration Number"
                  name="employerRegistrationNumber"
                  rules={[
                    {
                      pattern: /^\d{7}[A-Za-z]{1,2}$/,
                      message:
                        'Format: 7 digits + 1 or 2 letters (e.g. 1234567T or 1234567DH)',
                    },
                  ]}
                >
                  <AppInput placeholder="e.g. 1234567T or 1234567DH" />
                </Form.Item>
              </Col>
              <Col sm={24} md={12}>
                <Form.Item label="P12 Password" name="passwordP12">
                  <AppInput placeholder="Enter P12 password" type="password" />
                </Form.Item>
              </Col>
              <Col sm={24} md={12}>
                <Form.Item label="P12 Certificate" name="p12FileLink">
                  <AppUploadFile
                    urlFile={
                      typeof watchValues?.p12FileLink === 'string'
                        ? watchValues?.p12FileLink
                        : undefined
                    }
                    onChange={(file: File) => {
                      form.setFieldsValue({ p12FileLink: file });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </AppFormContainer>
      </div>
    </div>
  );
};

export default RevenueSettings;
